import { useEffect } from 'react';
import { updateCache } from '@/shared/lib/useCachedQuery';
import { updateConversationSettings } from '../api/userApi';
import type { CurrentUser } from './types';
import { CURRENT_USER_CACHE_KEY } from './useCurrentUser';

/**
 * Сообщает бэкенду часовой пояс браузера — один раз, и только если он
 * разошёлся с сохранённым.
 *
 * Нужен памяти тьютора: по нему считаются «сейчас у них вечер» в промпте и
 * дата нити («экзамен в пятницу» — какая именно пятница). Без него сервер
 * считал бы по UTC, то есть для Москвы на три часа мимо, а сказанное поздно
 * вечером — ещё и на день назад.
 *
 * Спрашиваем платформу, а не человека: `Intl` знает пояс точно, разрешений не
 * требует и не ошибается, в отличие от выпадающего списка в настройках.
 * Смена пояса (перелёт) подхватится сама при следующем запуске приложения.
 */
export function useReportTimezone(user: CurrentUser | null): void {
  const known = user?.timezone ?? null;
  const userId = user?.id;

  useEffect(() => {
    if (userId === undefined) return;

    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected || detected === known) return;

    // Молча: не сообщить пояс — не повод показывать человеку ошибку, память
    // просто останется на UTC до следующего запуска.
    // После отправки правим закешированный `/me`: иначе следующий заход на
    // корень увидел бы старый пояс и отправил бы его ещё раз.
    void updateConversationSettings({ timezone: detected })
      .then(() => updateCache<CurrentUser>(CURRENT_USER_CACHE_KEY, (current) => ({ ...current, timezone: detected })))
      .catch(() => undefined);
  }, [userId, known]);
}
